import { ofType } from 'redux-observable';
import { mergeMap, map, filter, switchMap, EMPTY, of } from 'rxjs';
import { AppEpic, AppState } from 'src/store';
import {
  inputIsNotNullOrUndefined,
  isScopeDefined,
  isWorklistActive,
  maybeGetComponentProps,
  topMemInUrl,
} from 'src/utils/Functions/epicsFunctions';
import { get, isEmpty } from 'lodash';
import { ConfDefnComponentType, EditableOverTimeGridComponent } from 'configuration-schemas/confdefnComponents';

import {
  fetchOverTimeData,
  updateSelectedItem,
  refreshOvertimeData,
  fetchCompanionData,
  fetchWorklistOverTimeData,
} from 'src/pages/AssortmentBuild/Pricing/PricingOverTime.slice';
import { receiveScopeRefreshTrigger } from 'src/components/AssortmentScopeSelector/AssortmentScopeSelector.slice';
import { receiveFilterStateAfterSubmission } from 'src/components/FilterPanel/FilterPanel.slice';
import { fetchMassEditData } from 'src/pages/AssortmentBuild/Pricing/PricingOverTime.slice';
import { LENS_ASST_PLAN_REFRESHING_PAGE } from '../Planning.types';
import { updateSelectedItem as worklistUpdateSelectedItem } from 'src/pages/Worklist/Worklist.slice';
import { REQUEST_EXTERNAL_DATA_REFRESH } from 'src/components/higherOrder/GlobalDataRefresh';

function fetchTopMember(state: AppState, worklistSelectedItem?: string): string {
  const flowSheetSelectedItem = !isEmpty(state.pages.assortmentBuild.pricing.selectedItem)
    ? state.pages.assortmentBuild.pricing.selectedItem
    : null;
  const worklistItem = worklistSelectedItem;
  const urlTopMember = topMemInUrl();
  return flowSheetSelectedItem ?? worklistItem ?? urlTopMember ?? '';
}

export const pricingOverTimeLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      updateSelectedItem.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
      refreshOvertimeData.type,
      worklistUpdateSelectedItem.type,
    ),
    filter((action) => {
      if (action.type === updateSelectedItem.type && isEmpty(action.payload)) {
        return false;
      }
      return true;
    }),
    map(() => {
      return (
        maybeGetComponentProps<EditableOverTimeGridComponent>(state$.value, ConfDefnComponentType.pricing) ??
        maybeGetComponentProps<EditableOverTimeGridComponent>(state$.value, 'pricing' as ConfDefnComponentType)
      );
    }),
    filter(inputIsNotNullOrUndefined),
    filter(() => isScopeDefined(state$.value.scope)),
    mergeMap((componentProps) => {
      const { topMembers } = componentProps;
      const currentSelectedItem = fetchTopMember(state$.value, topMembers);

      if (isEmpty(currentSelectedItem)) {
        return EMPTY;
      }

      // we allow a third element to be passed in from `componentProps.defns.models` in flowsheet/pricing,
      // to override the modeldefn, in order to support different pivot runs
      const maybeThirdModel = get(componentProps, 'defns.models[2]', undefined);
      return isWorklistActive(state$.value)
        ? of(fetchWorklistOverTimeData(currentSelectedItem, maybeThirdModel))
        : of(fetchOverTimeData(currentSelectedItem, maybeThirdModel));
    }),
  );
};

export const pricingOvertimeCompanionLoad: AppEpic = (action$, state$) => {
  return action$.pipe(
    ofType(
      receiveScopeRefreshTrigger.type,
      LENS_ASST_PLAN_REFRESHING_PAGE,
      receiveFilterStateAfterSubmission.type,
      REQUEST_EXTERNAL_DATA_REFRESH,
    ),
    map(() => maybeGetComponentProps<EditableOverTimeGridComponent>(state$.value, ConfDefnComponentType.pricing)),
    filter(inputIsNotNullOrUndefined),
    filter(() => !isWorklistActive(state$.value)),
    filter(() => isScopeDefined(state$.value.scope)),
    switchMap(({ defns, hideCompanion }) => {
      if (hideCompanion) {
        return of(refreshOvertimeData());
      }

      const companionModel = defns.models[0];
      const massEditModel = defns.models[1];
      const actions = [
        ...(companionModel ? [fetchCompanionData(companionModel)] : []),
        ...(massEditModel ? [fetchMassEditData(massEditModel)] : []),
      ];

      return of(...actions);
    }),
  );
};
